import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { GroupedInvestmentCategory } from "../../types";
import { ROLES_ENUM, SVG_ENUM, PACKET_ENUM, ROUTE_ENUM } from "enums";
import { useGetUnviewedPostsCount } from "hooks/posts";
import {
  ASSET_CATEGORY_NAMES,
  AssetCategorySlug,
} from "enums/AssetCategoryEnum";
import SVG from "shared/SVG";
import { Tooltip } from "react-tooltip";
import ContextMenu from "shared/ContextMenu";
import CompanyStatus from "shared/CompanyStatus";
import { CompanyInfoModal, CompanyDetailsModal } from "features/companies";
import InvestmentPhotoLightbox from "../InvestmentPhotoLightbox/InvestmentPhotoLightbox";
import InvestmentTransactionsSideModal from "../InvestmentTransactionsSideModal/InvestmentTransactionsSideModal";
import AssetDetailsSideModal from "../AssetDetailsSideModal/AssetDetailsSideModal";
import AddInvestmentTransactionModal from "../AddInvestmentTransactionModal/AddInvestmentTransactionModal";
import CloseInvestmentModal from "../CloseInvestmentModal/CloseInvestmentModal";
import {
  CLOSE_INVESTMENT_CONFIGS,
  CloseInvestmentModalConfig,
} from "../CloseInvestmentModal/closeInvestmentConfigs";
import DeleteInvestmentModal from "../DeleteInvestmentModal/DeleteInvestmentModal";
import EditInvestmentModal from "../EditInvestmentModal/EditInvestmentModal";
import { InvestmentTransactionType } from "hooks/investments/GetInvestmentTransactionsHook";
import { useUpdateInvestmentStatus } from "hooks/investments";
import { useUpdateCompany } from "hooks/companies";
import { queryClient } from "App";
import { useGetIndustriesHook } from "hooks/industries";
import { useGetMe } from "hooks/auth";
import TransactionsSideModal from "features/companies/TransactionsSideModal/TransactionsSideModal";
import {
  AddCompanyModal,
  AddCompanyClientModal,
  DeleteCompanyModal,
  SellSharesModal,
} from "features/companies";
import { AddSharesModal } from "features/shares";
import { getColumns, columnLabels, ColumnDef } from "./assetTileColumns";
import { formatValue } from "./assetTileFormatters";
import { getContextMenuItems } from "./assetTileContextMenuItems";
import SellAssetAdminModal from "../SellAssetAdminModal/SellAssetAdminModal";
import "./AssetTile.scss";

const SLUGS_WITH_TRANSACTIONS = ["crypto", "currencies"];

interface AssetTileProps {
  category: GroupedInvestmentCategory;
  index: number;
  isOwn?: boolean;
  isArchived?: boolean;
  onToggleExpand: (slug: string, isExpanded: boolean) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragEnter: (e: React.DragEvent<HTMLDivElement>, index: number) => void;
  onDragEnd: () => void;
  onTouchStart: (index: number) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  isDragging: boolean;
  isDragOver: boolean;
  isDragEnabled: boolean;
  isCollapseEnabled?: boolean;
  blurRestrictedData?: boolean;
  pendingOpenUuid?: string | null;
  pendingOpenCurrencyUuid?: string | null;
  onPendingOpenComplete?: () => void;
  onSellAllSuccess?: (uuid: string) => void;
  onEditArchiveSuccess?: (uuid: string) => void;
  hideContextMenu?: boolean;
  showCompanyDetails?: boolean;
}

const AssetTile = ({
  category,
  index,
  isOwn = false,
  isArchived = false,
  onToggleExpand,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isDragging,
  isDragOver,
  isDragEnabled,
  isCollapseEnabled = true,
  blurRestrictedData = false,
  pendingOpenUuid,
  pendingOpenCurrencyUuid,
  onPendingOpenComplete,
  onSellAllSuccess,
  onEditArchiveSuccess,
  hideContextMenu = false,
  showCompanyDetails = false,
}: AssetTileProps) => {
  const { investmentType, isExpanded, items } = category;
  const effectiveExpanded = isCollapseEnabled ? isExpanded : true;
  const [lightboxItem, setLightboxItem] = useState<any>(null);
  const [selectedTransactionItem, setSelectedTransactionItem] =
    useState<any>(null);
  const [selectedDetailsItem, setSelectedDetailsItem] = useState<any>(null);
  const [addTransactionItem, setAddTransactionItem] = useState<{
    item: any;
    defaultType: "buy" | "sell";
    currentQuantity?: number;
  } | null>(null);
  const [editTransactionItem, setEditTransactionItem] = useState<{
    item: any;
    tx: InvestmentTransactionType;
    currentQuantity: number;
  } | null>(null);
  const [closeItem, setCloseItem] = useState<{
    item: any;
    config: CloseInvestmentModalConfig;
  } | null>(null);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [deleteShareItem, setDeleteShareItem] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [selectedShareItem, setSelectedShareItem] = useState<any>(null);
  const [companyInfoItem, setCompanyInfoItem] = useState<any>(null);
  const [sellSharesItem, setSellSharesItem] = useState<any>(null);
  const [editSharesItem, setEditSharesItem] = useState<any>(null);
  const [editCompanyItem, setEditCompanyItem] = useState<any>(null);
  const [sellAssetAdminItem, setSellAssetAdminItem] = useState<any>(null);
  const [companyDetailsModalItem, setCompanyDetailsModalItem] =
    useState<any>(null);
  // ref pozwalający wrócić do sidebara po zamknięciu modali buy/sell/edit
  const shareParentRef = useRef<any>(null);
  const fromCompanyInfoRef = useRef(false);
  const fromCompanyDetailsRef = useRef(false);
  const tileRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchDraggingRef = useRef(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const preventScrollRef = useRef<((e: TouchEvent) => void) | null>(null);
  const nativeMoveRef = useRef<((e: TouchEvent) => void) | null>(null);
  const nativeEndRef = useRef<((e: TouchEvent) => void) | null>(null);
  const onTouchMoveLatest = useRef(onTouchMove);
  const onTouchEndLatest = useRef(onTouchEnd);
  useEffect(() => {
    onTouchMoveLatest.current = onTouchMove;
  }, [onTouchMove]);
  useEffect(() => {
    onTouchEndLatest.current = onTouchEnd;
  }, [onTouchEnd]);

  const cleanupNativeListeners = () => {
    if (preventScrollRef.current) {
      document.removeEventListener("touchmove", preventScrollRef.current);
      preventScrollRef.current = null;
    }
    if (nativeMoveRef.current) {
      document.removeEventListener("touchmove", nativeMoveRef.current);
      nativeMoveRef.current = null;
    }
    if (nativeEndRef.current) {
      document.removeEventListener("touchend", nativeEndRef.current);
      nativeEndRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      cleanupNativeListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTouchStartLocal = (e: React.TouchEvent) => {
    isTouchDraggingRef.current = false;
    touchStartPosRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    longPressTimerRef.current = setTimeout(() => {
      isTouchDraggingRef.current = true;
      onTouchStart(index);

      const preventScroll = (ev: TouchEvent) => {
        if (isTouchDraggingRef.current) ev.preventDefault();
      };
      preventScrollRef.current = preventScroll;
      document.addEventListener("touchmove", preventScroll, { passive: false });

      const nativeMove = (ev: TouchEvent) => {
        if (!isTouchDraggingRef.current) return;
        onTouchMoveLatest.current(ev as unknown as React.TouchEvent);
      };
      nativeMoveRef.current = nativeMove;
      document.addEventListener("touchmove", nativeMove, { passive: false });

      const nativeEnd = () => {
        if (isTouchDraggingRef.current) {
          isTouchDraggingRef.current = false;
          onTouchEndLatest.current();
        }
        touchStartPosRef.current = null;
        cleanupNativeListeners();
      };
      nativeEndRef.current = nativeEnd;
      document.addEventListener("touchend", nativeEnd);
    }, 350);
  };

  const handleTouchMoveLocal = (e: React.TouchEvent) => {
    if (!isTouchDraggingRef.current) {
      if (touchStartPosRef.current) {
        const dx = e.touches[0].clientX - touchStartPosRef.current.x;
        const dy = e.touches[0].clientY - touchStartPosRef.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
          if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
          }
        }
      }
    }
  };

  const handleTouchEndLocal = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    if (!isTouchDraggingRef.current) {
      touchStartPosRef.current = null;
    }
  };

  const isStocksOrShares =
    investmentType.slug === "stocks" || investmentType.slug === "shares";

  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === ROLES_ENUM.ADMIN;

  const navigate = useNavigate();
  const companyIds = isStocksOrShares
    ? items.map((item: any) => item.id).filter((id: any) => !!id)
    : [];
  const { data: unviewedPostCount } = useGetUnviewedPostsCount({
    companyIds,
    enabled: isStocksOrShares && companyIds.length > 0,
  });
  const hasAccessToSpeaker = isOwn
    ? authorizedUser?.package !== PACKET_ENUM.BASIC &&
      authorizedUser?.package !== PACKET_ENUM.STANDARD
    : authorizedUser?.package !== PACKET_ENUM.BASIC;
  const totalUnviewedPosts =
    unviewedPostCount && isStocksOrShares
      ? Object.values(unviewedPostCount as Record<string, number>).reduce(
          (sum: number, count: number) => sum + count,
          0,
        )
      : 0;
  const handleSpeakerClick = (companyId?: number, origin?: string) => {
    if (!hasAccessToSpeaker) return;
    if (companyId) {
      sessionStorage.setItem("selectedPostsCompany", companyId.toString());
    }
    if (origin) {
      sessionStorage.setItem("selectedPostsCompanyOrigin", origin);
    }
    navigate(ROUTE_ENUM.MARKET_INFORMATION);
  };

  const { data: industriesData } = useGetIndustriesHook();
  const industryMap: Record<string, string> = {};
  if (industriesData) {
    for (const ind of industriesData) {
      industryMap[ind.value] = ind.label;
    }
  }

  const { mutateAsync: updateInvestmentStatus } = useUpdateInvestmentStatus();
  const { mutateAsync: updateCompany } = useUpdateCompany();

  const handleInvestmentStatusChange = async (
    item: any,
    status: "active" | "withdrawn" | "hidden",
  ) => {
    if (isStocksOrShares) {
      await updateCompany({ id: item.id, status });
      queryClient.invalidateQueries({ queryKey: ["investments/grouped"] });
      queryClient.invalidateQueries({
        queryKey: ["investments/admin/grouped"],
      });
    } else {
      await updateInvestmentStatus({ uuid: item.uuid, status });
    }
  };

  const openSellModal = (shareItem: any) => {
    shareParentRef.current = shareItem;
    setSelectedShareItem(null);
    setTimeout(() => setSellSharesItem(shareItem), 100);
  };

  const openAddSharesModal = (shareItem: any) => {
    shareParentRef.current = shareItem;
    fromCompanyInfoRef.current = true;
    setEditSharesItem(shareItem);
  };

  const openEditSharesModal = (company: any) => {
    shareParentRef.current = selectedShareItem ?? shareParentRef.current;
    setSelectedShareItem(null);
    setTimeout(() => setEditSharesItem(company), 100);
  };

  const returnToSidebar = (data?: any) => {
    if (!isArchived && data?.availableShares === 0) {
      const uuid =
        shareParentRef.current?.companyUuid ?? shareParentRef.current?.uuid;
      setSellSharesItem(null);
      setEditSharesItem(null);
      setSelectedShareItem(null);
      fromCompanyDetailsRef.current = false;
      if (uuid) {
        onSellAllSuccess?.(uuid);
      }
      return;
    }
    if (isArchived && data?.availableShares > 0) {
      const uuid =
        shareParentRef.current?.companyUuid ?? shareParentRef.current?.uuid;
      setSellSharesItem(null);
      setEditSharesItem(null);
      setSelectedShareItem(null);
      fromCompanyDetailsRef.current = false;
      if (uuid) {
        onEditArchiveSuccess?.(uuid);
      }
      return;
    }
    if (data?.availableShares !== undefined && shareParentRef.current) {
      shareParentRef.current = {
        ...shareParentRef.current,
        sharesCount: data.availableShares,
        availableShares: data.availableShares,
      };
    }
    setSellSharesItem(null);
    setEditSharesItem(null);
    if (fromCompanyDetailsRef.current) {
      fromCompanyDetailsRef.current = false;
      if (shareParentRef.current)
        setTimeout(
          () => setCompanyDetailsModalItem({ ...shareParentRef.current }),
          100,
        );
    } else {
      if (shareParentRef.current)
        setTimeout(
          () => setSelectedShareItem({ ...shareParentRef.current }),
          100,
        );
    }
  };

  const hasTransactions = SLUGS_WITH_TRANSACTIONS.includes(investmentType.slug);

  useEffect(() => {
    if (!pendingOpenUuid) return;
    const matching = items.filter((item: any) => {
      if (item.uuid !== pendingOpenUuid) return false;
      if (pendingOpenCurrencyUuid)
        return item.currencyUuid === pendingOpenCurrencyUuid;
      return true;
    });
    if (!matching.length) return;
    const found = matching[0];

    if (isCollapseEnabled && !isExpanded) {
      onToggleExpand(investmentType.slug, true);
    }

    if (isStocksOrShares) {
      if (isAdmin && !blurRestrictedData) setCompanyInfoItem(found);
      else if (showCompanyDetails) setCompanyDetailsModalItem(found);
      else setSelectedShareItem(found);
    } else if (hasTransactions) {
      if (isAdmin && !blurRestrictedData) setSelectedTransactionItem(found);
      else if (!showCompanyDetails) setSelectedTransactionItem(found);
    } else setSelectedDetailsItem(found);

    setTimeout(() => {
      tileRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    onPendingOpenComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenUuid, pendingOpenCurrencyUuid, items]);

  useEffect(() => {
    if (selectedDetailsItem) {
      const updated = items.find(
        (item: any) => item.uuid === selectedDetailsItem.uuid,
      );
      if (updated) setSelectedDetailsItem(updated);
    }
    if (selectedTransactionItem) {
      const updated = items.find(
        (item: any) => item.uuid === selectedTransactionItem.uuid,
      );
      if (updated) setSelectedTransactionItem(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    const wrapper = contentWrapperRef.current;
    const inner = contentInnerRef.current;
    if (!wrapper || !inner) return;

    if (effectiveExpanded) {
      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName === "grid-template-rows") {
          inner.style.overflow = "visible";
        }
      };
      wrapper.addEventListener("transitionend", onTransitionEnd, {
        once: true,
      });
      return () => {
        wrapper.removeEventListener("transitionend", onTransitionEnd);
      };
    } else {
      inner.style.overflow = "hidden";
    }
  }, [effectiveExpanded]);

  const handleToggle = () => {
    if (!isCollapseEnabled) return;
    onToggleExpand(investmentType.slug, !isExpanded);
  };

  const columns = blurRestrictedData
    ? [
        ...getColumns(investmentType.slug),
        {
          key: "access",
          width: 110,
          label: "Dostęp",
        },
        ...(isAdmin
          ? [
              {
                key: "client",
                width: 200,
                label: "Klient",
              },
            ]
          : []),
      ]
    : [
        ...getColumns(investmentType.slug),
        ...(isAdmin ? [{ key: "status", width: 130, label: "Status" }] : []),
      ];
  const hasFixedWidths = columns.some((col) => col.width !== undefined);
  const accessColumn = columns.find((col) => col.key === "access");
  const clientColumn = columns.find((col) => col.key === "client");
  const statusColumn = columns.find((col) => col.key === "status");
  const desktopColumns = columns.filter(
    (col) =>
      col.key !== "access" && col.key !== "client" && col.key !== "status",
  );

  const fmtVal = (item: any, column: string) =>
    formatValue(item, column, investmentType.slug, industryMap);

  const isAnyModalOpen = !!(
    selectedTransactionItem ||
    selectedDetailsItem ||
    addTransactionItem ||
    editTransactionItem ||
    closeItem ||
    deleteItem ||
    deleteShareItem ||
    editItem ||
    selectedShareItem ||
    companyInfoItem ||
    companyDetailsModalItem ||
    sellSharesItem ||
    editSharesItem ||
    editCompanyItem ||
    sellAssetAdminItem ||
    lightboxItem
  );
  const dragEnabled = isDragEnabled && !isAnyModalOpen;

  return (
    <>
      <Tooltip id="asset-info-tooltip" place="top" className="tooltip" />
      <div
        ref={tileRef}
        className={`asset-tile ${
          dragEnabled && isDragging ? "asset-tile--dragging" : ""
        } ${dragEnabled && isDragOver ? "asset-tile--drag-over" : ""} ${
          effectiveExpanded ? "asset-tile--expanded" : ""
        } ${!dragEnabled ? "asset-tile--drag-disabled" : ""} ${
          !authorizedUser?.package ||
          authorizedUser.package === PACKET_ENUM.BASIC
            ? "asset-tile--no-access"
            : ""
        }`}
        draggable={dragEnabled}
        onDragStart={
          dragEnabled
            ? (e) => {
                isDraggingRef.current = true;
                onDragStart(e, index);
              }
            : undefined
        }
        onDragEnter={dragEnabled ? (e) => onDragEnter(e, index) : undefined}
        onDragEnd={
          dragEnabled
            ? () => {
                isDraggingRef.current = false;
                onDragEnd();
              }
            : undefined
        }
        onDragOver={dragEnabled ? (e) => e.preventDefault() : undefined}
        data-tile-index={index}
        onTouchStart={dragEnabled ? handleTouchStartLocal : undefined}
        onTouchMove={dragEnabled ? handleTouchMoveLocal : undefined}
        onTouchEnd={dragEnabled ? handleTouchEndLocal : undefined}
      >
        <div
          className="asset-tile__header"
          onClick={isCollapseEnabled ? handleToggle : undefined}
        >
          {!hideContextMenu && (
            <div
              className="asset-tile__drag-handle"
              onClick={(e) => e.stopPropagation()}
            >
              {isDragEnabled && <SVG type={SVG_ENUM.DRAG_HANDLE} />}
            </div>
          )}
          <span className="asset-tile__title">
            {ASSET_CATEGORY_NAMES[investmentType.slug as AssetCategorySlug] ??
              investmentType.name}
          </span>
          {items.length > 0 && !(isAdmin && !blurRestrictedData) && (
            <div
              className="asset-tile__header-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`asset-tile__action-btn${
                  !isStocksOrShares || !hasAccessToSpeaker
                    ? " asset-tile__action-btn--disabled"
                    : ""
                }`}
                onClick={() => isStocksOrShares && handleSpeakerClick()}
              >
                <SVG type={SVG_ENUM.ANNOUCEMENT} />
                <span
                  className={`asset-tile__action-badge${
                    isStocksOrShares && totalUnviewedPosts > 0
                      ? " asset-tile__action-badge--new"
                      : ""
                  }`}
                >
                  {isStocksOrShares ? totalUnviewedPosts : 0}
                </span>
              </button>
            </div>
          )}
          {isCollapseEnabled && (
            <div
              className={`asset-tile__chevron ${
                effectiveExpanded ? "asset-tile__chevron--expanded" : ""
              }`}
            >
              <SVG type={SVG_ENUM.CHEVRON_DOWN} />
            </div>
          )}
        </div>

        <div
          ref={contentWrapperRef}
          className={`asset-tile__content-wrapper ${
            effectiveExpanded ? "asset-tile__content-wrapper--expanded" : ""
          }`}
        >
          <div ref={contentInnerRef} className="asset-tile__content-inner">
            {items.length > 0 && (
              <div className="asset-tile__content">
                <div className="asset-tile__table">
                  <div className="asset-tile__table-header">
                    {desktopColumns.map((col) => (
                      <div
                        key={col.key}
                        className="asset-tile__table-cell"
                        style={
                          col.width
                            ? {
                                flex: `0 0 ${col.width}px`,
                                minWidth: col.width,
                              }
                            : undefined
                        }
                      >
                        {col.label ?? columnLabels[col.key] ?? col.key}
                      </div>
                    ))}
                    {hasFixedWidths && <div style={{ flex: 1 }} />}
                    {clientColumn && (
                      <div
                        key={clientColumn.key}
                        className="asset-tile__table-cell"
                        style={{
                          flex: `0 0 ${clientColumn.width}px`,
                          minWidth: clientColumn.width,
                        }}
                      >
                        {clientColumn.label}
                      </div>
                    )}
                    {statusColumn && (
                      <div
                        key={statusColumn.key}
                        className="asset-tile__table-cell"
                        style={{
                          flex: `0 0 ${statusColumn.width}px`,
                          minWidth: statusColumn.width,
                        }}
                      >
                        {statusColumn.label}
                      </div>
                    )}
                    {accessColumn && (
                      <div
                        key={accessColumn.key}
                        className="asset-tile__table-cell"
                        style={
                          accessColumn.width
                            ? {
                                flex: `0 0 60px`,
                                minWidth: 60,
                              }
                            : undefined
                        }
                      >
                        {accessColumn.label ??
                          columnLabels[accessColumn.key] ??
                          accessColumn.key}
                      </div>
                    )}
                    {!blurRestrictedData && (
                      <div className="asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop" />
                    )}
                  </div>
                  {items.map((item) => {
                    const isHistoryNotAccepted =
                      (item as any)?.isHistoryAccepted === 0 ||
                      (item as any)?.isHistoryAccepted === "0";
                    const cannotViewReliance =
                      (item as any)?.relianceCanView === 0 ||
                      (item as any)?.relianceCanView === "0" ||
                      (item as any)?.relianceCanView === false;
                    const shouldBlurRow =
                      blurRestrictedData &&
                      (isHistoryNotAccepted || cannotViewReliance);

                    // const photoFiles =
                    //   investmentType.slug === "artwork" ||
                    //   investmentType.slug === "real_estate"
                    //     ? (item.files ?? []).filter(
                    //         (f: any) => f.role === "photo",
                    //       )
                    //     : [];

                    const renderCellContent = (col: ColumnDef) => {
                      if (col.key === "client") {
                        const owner = (item as any)?.owner;
                        const ownerName = owner?.name ?? "";
                        const ownerPackage = owner?.package;
                        return (
                          <span className="asset-tile__client">
                            <span className="asset-tile__client-name">
                              {ownerName || "—"}
                            </span>
                            {ownerPackage && (
                              <span className="asset-tile__client-badge">
                                {String(ownerPackage).toUpperCase()}
                              </span>
                            )}
                          </span>
                        );
                      }
                      if (col.key === "status") {
                        return <CompanyStatus status={(item as any).status} />;
                      }
                      if (col.key === "access") {
                        const hasAccess = !shouldBlurRow;

                        return (
                          <span
                            className={`asset-tile__access${
                              hasAccess
                                ? " asset-tile__access--yes"
                                : " asset-tile__access--no"
                            }`}
                          >
                            <SVG
                              type={
                                hasAccess ? SVG_ENUM.LOCK_OPEN : SVG_ENUM.LOCK
                              }
                              className="asset-tile__access-icon"
                            />
                            {hasAccess ? "Tak" : "Brak"}
                          </span>
                        );
                      }
                      if (col.value !== undefined) {
                        return <>{col.value}</>;
                      }
                      if (col.key === "primaryValue" && !isOwn && !isAdmin) {
                        const saleVal = (item as any).saleValue;
                        if (saleVal == null) return <>Brak</>;
                        const num = Number(saleVal);
                        const formatted = num
                          .toLocaleString("en-US", {
                            minimumFractionDigits: Number.isInteger(num)
                              ? 0
                              : 2,
                            maximumFractionDigits: 2,
                          })
                          .replace(/,/g, "\u00A0");
                        const suffix = col.computeSuffix
                          ? col.computeSuffix(item)
                          : col.suffix ?? "";
                        return (
                          <>
                            {formatted}
                            {suffix}
                          </>
                        );
                      }
                      if (
                        col.key === "name" &&
                        investmentType.slug === "real_estate"
                      ) {
                        return <span>{fmtVal(item, col.key)}</span>;
                      }
                      if (
                        col.key === "entity" &&
                        investmentType.slug === "loans" &&
                        shouldBlurRow
                      ) {
                        const loanType =
                          (item as any).loanType ??
                          (item as any).details?.loanType;
                        const LOAN_LABELS: Record<string, string> = {
                          borrowed: "Zaciągnięta",
                          lent: "Udzielona",
                        };
                        return (
                          <span>{LOAN_LABELS[loanType] ?? "Pożyczka"}</span>
                        );
                      }
                      return (
                        <>
                          {fmtVal(item, col.key) !== "Brak" && col.computePrefix
                            ? col.computePrefix(item)
                            : ""}
                          {fmtVal(item, col.key)}
                          {(() => {
                            if (fmtVal(item, col.key) === "Brak") return "";
                            if (col.computeSuffix) {
                              const s = col.computeSuffix(item);
                              return s ? s : "";
                            }
                            return col.suffix ?? "";
                          })()}
                        </>
                      );
                    };

                    const isInvestmentPolicy =
                      investmentType.slug === "investment_policy";
                    const isTreasuryBonds =
                      investmentType.slug === "treasury_bonds";
                    const isArtwork = investmentType.slug === "artwork";
                    const isPreciousStones =
                      investmentType.slug === "precious_stones";
                    const isPreciousMetals =
                      investmentType.slug === "precious_metals";
                    const isRealEstate = investmentType.slug === "real_estate";
                    const isLoan = investmentType.slug === "loans";
                    const isOther =
                      !isStocksOrShares &&
                      !isInvestmentPolicy &&
                      !isTreasuryBonds &&
                      !isArtwork &&
                      !isPreciousStones &&
                      !isPreciousMetals &&
                      !isRealEstate &&
                      !isLoan &&
                      !hasTransactions;
                    const isDetails = !isStocksOrShares && !hasTransactions;

                    const activeUuid =
                      selectedShareItem?.uuid ??
                      selectedDetailsItem?.uuid ??
                      selectedTransactionItem?.uuid ??
                      null;

                    const isActive = activeUuid === item.uuid;

                    return (
                      <div
                        key={`${item.uuid}_${
                          (item as any).shareId ??
                          (item as any).currencyUuid ??
                          index
                        }`}
                        className={`asset-tile__table-row${
                          (hasTransactions || isStocksOrShares || isDetails) &&
                          !shouldBlurRow
                            ? " asset-tile__table-row--clickable"
                            : ""
                        }${isActive ? " asset-tile__table-row--active" : ""}`}
                        onClick={() => {
                          if (isDraggingRef.current) return;
                          if (shouldBlurRow) return;
                          if (isStocksOrShares) {
                            if (isAdmin && !blurRestrictedData)
                              setCompanyInfoItem(item);
                            else if (showCompanyDetails)
                              setCompanyDetailsModalItem(item);
                            else setSelectedShareItem(item);
                          } else if (hasTransactions) {
                            if (!showCompanyDetails)
                              setSelectedTransactionItem(item);
                          } else setSelectedDetailsItem(item);
                        }}
                      >
                        {/* Desktop: pozioma tabela */}
                        {desktopColumns.map((col, colIndex) => (
                          <div
                            key={col.key}
                            className={`asset-tile__table-cell asset-tile__table-cell--desktop${
                              shouldBlurRow && colIndex > 0
                                ? " asset-tile__table-cell--blurred"
                                : ""
                            }`}
                            style={
                              col.width
                                ? {
                                    flex: `0 0 ${col.width}px`,
                                    minWidth: col.width,
                                  }
                                : undefined
                            }
                          >
                            {col.key === desktopColumns[0].key &&
                              (isStocksOrShares
                                ? item.description
                                : item.additionalInfo) && (
                                <span
                                  className="asset-tile__info-icon"
                                  data-tooltip-id="asset-info-tooltip"
                                  data-tooltip-content={
                                    isStocksOrShares
                                      ? item.description
                                      : item.additionalInfo
                                  }
                                >
                                  <SVG type={SVG_ENUM.INFO} />
                                </span>
                              )}
                            {renderCellContent(col)}
                          </div>
                        ))}
                        {hasFixedWidths && (
                          <div
                            className={`asset-tile__table-cell--desktop${
                              shouldBlurRow
                                ? " asset-tile__table-cell--blurred"
                                : ""
                            }`}
                            style={{ flex: 1 }}
                          />
                        )}
                        {clientColumn && (
                          <div
                            key={clientColumn.key}
                            className="asset-tile__table-cell asset-tile__table-cell--desktop"
                            style={{
                              flex: `0 0 ${clientColumn.width}px`,
                              minWidth: clientColumn.width,
                            }}
                          >
                            {renderCellContent(clientColumn)}
                          </div>
                        )}
                        {statusColumn && (
                          <div
                            key={statusColumn.key}
                            className="asset-tile__table-cell asset-tile__table-cell--desktop"
                            style={{
                              flex: `0 0 ${statusColumn.width}px`,
                              minWidth: statusColumn.width,
                            }}
                          >
                            {renderCellContent(statusColumn)}
                          </div>
                        )}
                        {accessColumn && (
                          <div
                            key={accessColumn.key}
                            className="asset-tile__table-cell asset-tile__table-cell--desktop"
                            style={
                              accessColumn.width
                                ? {
                                    flex: `0 0 60px`,
                                    minWidth: 60,
                                  }
                                : undefined
                            }
                          >
                            {renderCellContent(accessColumn)}
                          </div>
                        )}
                        {!blurRestrictedData && (
                          <div
                            className={`asset-tile__table-cell asset-tile__table-cell--actions asset-tile__table-cell--actions-desktop${
                              shouldBlurRow
                                ? " asset-tile__table-cell--blurred"
                                : ""
                            }`}
                          >
                            {!shouldBlurRow && (
                              <>
                                {!(isAdmin && !blurRestrictedData) && (
                                  <button
                                    className={`asset-tile__action-btn${
                                      !isStocksOrShares || !hasAccessToSpeaker
                                        ? " asset-tile__action-btn--disabled"
                                        : ""
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isStocksOrShares) {
                                        handleSpeakerClick(
                                          (item as any).id,
                                          item.origin,
                                        );
                                      }
                                    }}
                                  >
                                    <SVG type={SVG_ENUM.ANNOUCEMENT} />
                                    <span
                                      className={`asset-tile__action-badge${
                                        isStocksOrShares &&
                                        (unviewedPostCount?.[
                                          (item as any).id
                                        ] || 0) > 0
                                          ? " asset-tile__action-badge--new"
                                          : ""
                                      }`}
                                    >
                                      {isStocksOrShares
                                        ? unviewedPostCount?.[
                                            (item as any).id
                                          ] || 0
                                        : 0}
                                    </span>
                                  </button>
                                )}
                                {!hideContextMenu && (
                                  <ContextMenu
                                    data={item}
                                    position="left"
                                    items={getContextMenuItems({
                                      item,
                                      isOwn,
                                      isArchived,
                                      isAdmin,
                                      isStocksOrShares,
                                      hasTransactions,
                                      isOther,
                                      isPreciousMetals,
                                      isReliance:
                                        isAdmin && !blurRestrictedData,
                                      slug: investmentType.slug,
                                      setEditItem,
                                      setCloseItem,
                                      setDeleteItem,
                                      setDeleteShareItem,
                                      setAddTransactionItem,
                                      setEditCompanyItem,
                                      openSellModal,
                                      openAddSharesModal,
                                      setSellAssetAdminItem,
                                      handleStatusChange:
                                        handleInvestmentStatusChange,
                                    })}
                                  >
                                    <button className="asset-tile__action-btn">
                                      <SVG type={SVG_ENUM.DOTS} />
                                    </button>
                                  </ContextMenu>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        <div className="asset-tile__mobile-wrapper">
                          <div
                            className={`asset-tile__mobile-list${
                              (hasTransactions ||
                                isStocksOrShares ||
                                isDetails) &&
                              !shouldBlurRow
                                ? " asset-tile__mobile-list--clickable"
                                : ""
                            }`}
                            onClick={() => {
                              if (isDraggingRef.current) return;
                              if (shouldBlurRow) return;
                              if (isStocksOrShares) {
                                if (isAdmin && !blurRestrictedData)
                                  setCompanyInfoItem(item);
                                else if (showCompanyDetails)
                                  setCompanyDetailsModalItem(item);
                                else setSelectedShareItem(item);
                              } else if (hasTransactions) {
                                if (!showCompanyDetails)
                                  setSelectedTransactionItem(item);
                              } else setSelectedDetailsItem(item);
                            }}
                          >
                            {columns.map((col, colIndex) => (
                              <div
                                key={col.key}
                                className="asset-tile__mobile-row"
                              >
                                <span className="asset-tile__mobile-label">
                                  {col.label ??
                                    columnLabels[col.key] ??
                                    col.key}
                                </span>
                                <span
                                  className={`asset-tile__mobile-value${
                                    shouldBlurRow &&
                                    colIndex > 0 &&
                                    col.key !== "access" &&
                                    col.key !== "client"
                                      ? " asset-tile__mobile-value--blurred"
                                      : ""
                                  }`}
                                >
                                  {renderCellContent(col)}
                                </span>
                              </div>
                            ))}
                          </div>
                          {!blurRestrictedData && !hideContextMenu && (
                            <div
                              className="asset-tile__mobile-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {!shouldBlurRow && !hideContextMenu && (
                                <>
                                  {isOwn && !isStocksOrShares && (
                                    <button
                                      className="asset-tile__action-btn"
                                      onClick={() => setEditItem(item)}
                                    >
                                      <SVG type={SVG_ENUM.EDIT} />
                                    </button>
                                  )}
                                  {isStocksOrShares &&
                                    item.origin === "other" && (
                                      <button
                                        className="asset-tile__action-btn"
                                        onClick={() => setEditCompanyItem(item)}
                                      >
                                        <SVG type={SVG_ENUM.EDIT} />
                                      </button>
                                    )}
                                  {!isArchived &&
                                    (isInvestmentPolicy ||
                                      isTreasuryBonds ||
                                      isArtwork ||
                                      isPreciousStones ||
                                      isPreciousMetals ||
                                      isRealEstate ||
                                      isLoan ||
                                      isOther) && (
                                      <button
                                        className="asset-tile__action-btn"
                                        onClick={() =>
                                          setCloseItem({
                                            item,
                                            config: isInvestmentPolicy
                                              ? CLOSE_INVESTMENT_CONFIGS.policy
                                              : isTreasuryBonds
                                              ? CLOSE_INVESTMENT_CONFIGS.treasuryBonds
                                              : isPreciousStones
                                              ? CLOSE_INVESTMENT_CONFIGS.preciousStones
                                              : isPreciousMetals
                                              ? CLOSE_INVESTMENT_CONFIGS.preciousMetals
                                              : isRealEstate
                                              ? CLOSE_INVESTMENT_CONFIGS.realEstate
                                              : isLoan
                                              ? CLOSE_INVESTMENT_CONFIGS.loan
                                              : isArtwork
                                              ? CLOSE_INVESTMENT_CONFIGS.artwork
                                              : CLOSE_INVESTMENT_CONFIGS.otherAsset,
                                          })
                                        }
                                      >
                                        <SVG type={SVG_ENUM.CLOSE} />
                                      </button>
                                    )}
                                  {isOwn && !isStocksOrShares && (
                                    <button
                                      className="asset-tile__action-btn"
                                      onClick={() => setDeleteItem(item)}
                                    >
                                      <SVG type={SVG_ENUM.TRASH} />
                                    </button>
                                  )}
                                </>
                              )}
                              {!shouldBlurRow && isOwn && isStocksOrShares && (
                                <button
                                  className="asset-tile__action-btn"
                                  onClick={() => setDeleteShareItem(item)}
                                >
                                  <SVG type={SVG_ENUM.TRASH} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <CompanyInfoModal
          isOpen={!!companyInfoItem}
          onClose={() => setCompanyInfoItem(null)}
          company={companyInfoItem}
          setAddClient={(company) => {
            shareParentRef.current = company;
            fromCompanyInfoRef.current = true;
            setCompanyInfoItem(null);
            setTimeout(() => setEditSharesItem(company), 100);
          }}
          setEditTransaction={(share) => {
            shareParentRef.current = companyInfoItem;
            fromCompanyInfoRef.current = true;
            setCompanyInfoItem(null);
            setTimeout(
              () => setEditSharesItem({ ...share, editTransaction: true }),
              100,
            );
          }}
        />

        <TransactionsSideModal
          isOpen={!!selectedShareItem}
          onClose={() => setSelectedShareItem(null)}
          company={selectedShareItem}
          setEditTransaction={(company) => openEditSharesModal(company)}
          onAddTransactionClick={() => openSellModal(selectedShareItem)}
          isArchived={isArchived}
          isOwn={isOwn}
          blurRestrictedData={blurRestrictedData}
        />

        <CompanyDetailsModal
          isOpen={!!companyDetailsModalItem}
          onClose={() => setCompanyDetailsModalItem(null)}
          company={companyDetailsModalItem}
          setAddClient={(company) => {
            shareParentRef.current = company;
            fromCompanyDetailsRef.current = true;
            setCompanyDetailsModalItem(null);
            setTimeout(() => openSellModal(company), 100);
          }}
          setEditTransaction={(tx) => {
            shareParentRef.current = companyDetailsModalItem;
            fromCompanyDetailsRef.current = true;
            setCompanyDetailsModalItem(null);
            setTimeout(
              () => setEditSharesItem({ ...tx, editTransaction: true }),
              100,
            );
          }}
          companiesTab="reliance"
        />

        <SellSharesModal
          isOpen={!!sellSharesItem}
          onSuccess={returnToSidebar}
          onClose={returnToSidebar}
          hardClose={() => setSellSharesItem(null)}
          company={sellSharesItem}
        />

        <AddSharesModal
          isOpen={!!editSharesItem}
          onSuccess={(data) => {
            if (fromCompanyInfoRef.current) {
              fromCompanyInfoRef.current = false;
              setEditSharesItem(null);
              setTimeout(
                () => setCompanyInfoItem({ ...shareParentRef.current }),
                100,
              );
            } else if (fromCompanyDetailsRef.current) {
              fromCompanyDetailsRef.current = false;
              setEditSharesItem(null);
              setTimeout(
                () => setCompanyDetailsModalItem({ ...shareParentRef.current }),
                100,
              );
            } else {
              returnToSidebar(data);
            }
          }}
          onClose={() => {
            if (fromCompanyInfoRef.current) {
              fromCompanyInfoRef.current = false;
              setEditSharesItem(null);
              setTimeout(
                () => setCompanyInfoItem({ ...shareParentRef.current }),
                100,
              );
            } else if (fromCompanyDetailsRef.current) {
              fromCompanyDetailsRef.current = false;
              setEditSharesItem(null);
              setTimeout(
                () => setCompanyDetailsModalItem({ ...shareParentRef.current }),
                100,
              );
            } else {
              returnToSidebar();
            }
          }}
          hardClose={() => {
            fromCompanyInfoRef.current = false;
            fromCompanyDetailsRef.current = false;
            setEditSharesItem(null);
          }}
          company={editSharesItem}
        />

        <InvestmentPhotoLightbox
          isOpen={!!lightboxItem}
          onClose={() => setLightboxItem(null)}
          files={(lightboxItem?.files ?? []).filter(
            (f: any) => f.role === "photo",
          )}
          title={lightboxItem?.name}
        />

        <InvestmentTransactionsSideModal
          isOpen={!!selectedTransactionItem}
          onClose={() => setSelectedTransactionItem(null)}
          item={selectedTransactionItem}
          investmentType={investmentType}
          isOwn={isOwn}
          isArchived={isArchived}
          onAddTransaction={(type, currentQuantity) =>
            setAddTransactionItem({
              item: selectedTransactionItem,
              defaultType: type,
              currentQuantity,
            })
          }
          onEditTransaction={(tx, currentQuantity) =>
            setEditTransactionItem({
              item: selectedTransactionItem,
              tx,
              currentQuantity,
            })
          }
        />

        <AssetDetailsSideModal
          isOpen={!!selectedDetailsItem}
          onClose={() => setSelectedDetailsItem(null)}
          item={selectedDetailsItem}
          slug={investmentType.slug}
          investmentTypeName={investmentType.name}
          isOwn={isOwn}
          hidePriceHeader={hideContextMenu}
          onViewTransactions={
            hasTransactions && !showCompanyDetails
              ? () => {
                  const item = selectedDetailsItem;
                  setSelectedDetailsItem(null);
                  setTimeout(() => setSelectedTransactionItem(item), 100);
                }
              : undefined
          }
        />

        <AddInvestmentTransactionModal
          isOpen={!!addTransactionItem}
          onClose={() => setAddTransactionItem(null)}
          onSuccess={({ isSellAll }) => {
            const uuid = addTransactionItem?.item?.uuid;
            if (!isArchived && isSellAll && uuid) {
              setSelectedTransactionItem(null);
              onSellAllSuccess?.(uuid);
            } else {
              setSelectedTransactionItem(addTransactionItem?.item ?? null);
            }
          }}
          item={addTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={addTransactionItem?.defaultType ?? "sell"}
          isOwn={isOwn}
          isArchived={isArchived}
          currentQuantity={addTransactionItem?.currentQuantity}
        />

        <AddInvestmentTransactionModal
          isOpen={!!editTransactionItem}
          onClose={() => setEditTransactionItem(null)}
          onSuccess={() => {
            const uuid = editTransactionItem?.item?.uuid;
            if (isArchived && uuid) {
              setSelectedTransactionItem(null);
              onEditArchiveSuccess?.(uuid);
            }
          }}
          item={editTransactionItem?.item ?? null}
          investmentType={investmentType}
          defaultType={editTransactionItem?.tx?.type ?? "sell"}
          isOwn={false}
          currentQuantity={editTransactionItem?.currentQuantity}
          editTransaction={editTransactionItem?.tx ?? null}
        />

        <CloseInvestmentModal
          isOpen={!!closeItem}
          onClose={() => setCloseItem(null)}
          onSuccess={(uuid) => {
            setCloseItem(null);
            onSellAllSuccess?.(uuid);
          }}
          item={closeItem?.item ?? null}
          config={closeItem?.config ?? CLOSE_INVESTMENT_CONFIGS.otherAsset}
        />

        <DeleteInvestmentModal
          isOpen={!!deleteItem}
          onClose={() => setDeleteItem(null)}
          item={deleteItem}
        />

        <DeleteCompanyModal
          isOpen={!!deleteShareItem}
          onClose={() => setDeleteShareItem(null)}
          company={deleteShareItem}
        />

        <EditInvestmentModal
          isOpen={!!editItem}
          onClose={() => setEditItem(null)}
          item={editItem}
          slug={investmentType.slug}
        />

        <AddCompanyModal
          isOpen={!!editCompanyItem && isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />

        <AddCompanyClientModal
          isOpen={!!editCompanyItem && !isAdmin}
          onClose={() => setEditCompanyItem(null)}
          company={editCompanyItem}
          onSuccess={() => setEditCompanyItem(null)}
        />

        <SellAssetAdminModal
          isOpen={!!sellAssetAdminItem}
          onClose={() => setSellAssetAdminItem(null)}
          hardClose={() => setSellAssetAdminItem(null)}
          onSuccess={() => setSellAssetAdminItem(null)}
          item={sellAssetAdminItem}
        />
      </div>
    </>
  );
};

export default AssetTile;
